import { UploadingIcon } from "@/icons";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import {
  UploadProgressButton,
  UploadProgressCardActions,
  UploadProgressCardRow,
  UploadProgressIconWrapper,
  UploadProgressInfo,
  UploadProgressMessage,
  UploadProgressNetworkStatus,
  UploadProgressNumData,
  UploadProgressSecondaryInfo,
  UploadProgressSecondButton,
  UploadProgressSpeedDot,
  UploadProgressSpeedLabel,
  UploadProgressSpeedValue,
  UploadProgressTitle,
} from "./UploadProgress.elements";
import { intervalToDuration } from "date-fns";
import { Box, Chip, CircularProgress, Collapse, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateUploadingPackage, useActiveUploads, useAuthState, usePackagesActions, UploadClientStatus, useUploadQueueState } from "@/store";
import { useChunkedUploadFiles, useNetworkQuality, usePendingClick, useUploadOwnership, useUploadTelemetry } from "@/components/hooks";
import { ChevronIcon } from "@/icons";
import {
  NETWORK_QUALITY,
  getNetworkQualityMessage,
  getNetworkQualitySeverity,
} from "@/utils/networkQuality";
import { getTelemetryDisplay } from "@/utils/uploadTelemetry";
import { isChunkedUploaderActive } from "@/Service/UploaderToggle";
import {
  hasAllFsHandles,
  requestRehydration,
  supportsDirectoryPicker,
  supportsFsHandles,
} from "@/utils/uploadPersistence";
import { queryUploadOwnership } from "@/utils/uploadOwnership";
import { UploadFileRows } from "./UploadFileRows";
import { CancelUploadDialog } from "./CancelUploadDialog";
import { computeEffectiveTelemetry } from "./effectiveTelemetry";

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = 1024 * 1024;
const BYTES_IN_GB = BYTES_IN_MB * 1024;

// Hysteresis thresholds for the "show uploaded in KB when the connection is slow" toggle.
// Two thresholds prevent the display flipping unit every ~2s as rollingBps jitters across a
// single boundary — enter KB mode below LOW, only leave KB mode when speed clearly recovers
// above HIGH. The gap (100→300 KB/s) was picked from real client rollingBps traces in the
// 2026-07-29 stuck-upload investigation, where "stuck-looking" streams sat near 60–100 KB/s.
const KB_MODE_ENTER_BPS = 100 * BYTES_IN_KB;
const KB_MODE_EXIT_BPS = 300 * BYTES_IN_KB;

export const formatRemainingTime = (seconds) => {
  if (!seconds) return "";

  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

  return [
    duration.hours ? `${duration.hours}h ` : "",
    duration.minutes ? `${duration.minutes}m ` : "",
    duration.seconds ? `${duration.seconds}s` : "",
  ].join("");
};

const formatSpeed = (bps) => {
  if (typeof bps !== "number" || !Number.isFinite(bps)) {
    return null;
  }

  const units = ["B/s", "KB/s", "MB/s", "GB/s"];
  let value = Math.max(0, bps);
  let unitIndex = 0;

  if (value === 0) {
    return "0 B/s";
  }

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : value >= 1 ? 1 : 2;

  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
};

// Format `uploadedBytes` in KB with a thousands-separator ("5,005 KB"). Used for the slow-mode
// display so a user on 60 KB/s uplink sees the value tick up every second instead of every
// 20s that MB-precision would give them.
const formatKb = (bytes) => `${Math.floor(Math.max(0, bytes) / BYTES_IN_KB).toLocaleString()} KB`;

const formatUploadedInfo = (telemetry, { showUploadedInKb = false } = {}) => {
  if (
      !telemetry ||
      typeof telemetry.uploadedBytes !== "number" ||
      telemetry.uploadedBytes < 0
  ) {
    return null;
  }

  const { uploadedBytes, totalBytes } = telemetry;
  const hasTotal = typeof totalBytes === "number" && totalBytes > 0;
  const useGigabytes =
      (hasTotal && totalBytes >= BYTES_IN_GB) ||
      (!hasTotal && uploadedBytes >= BYTES_IN_GB);

  const unitBase = useGigabytes ? BYTES_IN_GB : BYTES_IN_MB;
  const unitLabel = useGigabytes ? "GB" : "MB";

  const formatValue = (value) => {
    if (value >= 100) return value.toFixed(0);
    if (value >= 10) return value.toFixed(1);

    return value.toFixed(2);
  };

  // Slow-mode: show the uploaded portion in KB (thousands-separated) while keeping the total
  // in its natural MB/GB unit — "5,005 KB of 100 MB". This is deliberately asymmetric: total
  // is stable so it stays in the coarse unit, uploaded ticks in the fine unit so the user has
  // visible per-second motion. Slice back to matched-units once speed recovers above the exit
  // threshold (see KB_MODE_ENTER/EXIT constants above).
  const uploadedLabel = showUploadedInKb
      ? formatKb(uploadedBytes)
      : `${formatValue(uploadedBytes / unitBase)} ${unitLabel}`;

  if (hasTotal) {
    const totalLabel = `${formatValue(totalBytes / unitBase)} ${unitLabel}`;

    return `${uploadedLabel} of ${totalLabel}`;
  }

  return uploadedLabel;
};

// Mirrors backend config.upload.tus.session.stuck-paused-hours (default 12). Update in
// lockstep with TUS_STUCK_PAUSED_HOURS if that changes — a mismatch here shows a countdown
// the backend will not honour (either resume fails earlier than promised, or the card claims
// the upload is expired while the server would still accept a resume).
export const PAUSED_UPLOAD_TTL_HOURS = 12;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// Walk a FileSystemDirectoryHandle into a flat File list. showDirectoryPicker only hands back
// the top-level handle, and contributors routinely organise a package into subfolders (camera
// A / camera B), so a non-recursive read would silently miss files and the resume would fail
// the fingerprint check with a confusing "couldn't find N file(s)". Depth-capped because a
// user can point us at a huge tree (e.g. their home directory) by mistake.
const MAX_DIRECTORY_DEPTH = 5;

const collectFilesFromDirectory = async (dirHandle, depth = 0) => {
  if (!dirHandle || depth > MAX_DIRECTORY_DEPTH) return [];

  const collected = [];

  for await (const entry of dirHandle.values()) {
    if (entry.kind === "file") {
      try {
        collected.push(await entry.getFile());
      } catch {
        // Unreadable entry (permission, or the file vanished since the picker listed it).
        // Skip it — the fingerprint check downstream reports what's actually missing.
      }
    } else if (entry.kind === "directory") {
      collected.push(...(await collectFilesFromDirectory(entry, depth + 1)));
    }
  }

  return collected;
};

export const formatPausedExpiry = (pausedAt) => {
  if (!pausedAt) return null;

  const remainingMs = pausedAt + PAUSED_UPLOAD_TTL_HOURS * MS_PER_HOUR - Date.now();

  if (remainingMs <= 0) return "Expired — please cancel and start a new upload.";

  const remainingDays = Math.floor(remainingMs / MS_PER_DAY);

  if (remainingDays >= 1) {
    return `Expires in ${remainingDays} day${remainingDays === 1 ? "" : "s"}.`;
  }

  const remainingHours = Math.max(1, Math.floor(remainingMs / MS_PER_HOUR));

  return `Expires in ${remainingHours} hour${remainingHours === 1 ? "" : "s"}.`;
};

export const UploadProgressLoadingCard = ({ upload, compact = false }) => {
  const [isConfirmCancel, setIsConfirmCancel] = useState(false);
  const [isFilesExpanded, setIsFilesExpanded] = useState(false);

  const filesForSummary = useChunkedUploadFiles(upload.uploadId);

  const filesSummary = useMemo(() => {
    if (!filesForSummary || filesForSummary.length === 0) return null;

    const total = filesForSummary.length;
    const done = filesForSummary.filter((f) => f.status === "uploaded").length;

    return { total, done };
  }, [filesForSummary]);

  // Worst-of stall state across all files in the package. Fed by the ChunkedUploadService
  // stall watchdog which polls per-file for onProgress silence. Priority ordering:
  // reconnecting (currently mid abort+restart) > stalled (15s of silence, offer manual retry)
  // > hint (5s of silence, subtle "waiting" text). Suppressed while paused because the pause
  // itself is intentional silence and would otherwise show a misleading "stalled" banner.
  const stallStatus = useMemo(() => {
    if (!filesForSummary || filesForSummary.length === 0) return null;
    let worst = null;
    for (const f of filesForSummary) {
      if (f.stallState === "reconnecting") return "reconnecting";
      if (f.stallState === "stalled") worst = "stalled";
      else if (f.stallState === "hint" && worst !== "stalled") worst = "hint";
    }
    return worst;
  }, [filesForSummary]);

  const {
    cancelUpload,
    pauseAllUploads,
    resumeAllUploads,
    rehydratedResumeUpload,
  } = usePackagesActions();

  const networkQuality = useNetworkQuality();
  const telemetry = useUploadTelemetry(upload.uploadId);

  // Treat a telemetry-level "paused" as authoritative for the visual paused
  // state too, not just Redux upload.status. Prod bug 2026-08: after long
  // sleep or transient network failure the auto-pause path calls
  // uploadTelemetryService.pause (banner correctly says "Upload paused.") but
  // Redux upload.status can remain UPLOADING when the wrapped error is caught
  // upstream as a generic TUS_UPLOAD_FAILED → VERIFYING → RECEIVED/PROCESSING
  // → UPLOADING round-trip. Without this OR the card kept showing the pause
  // icon + primary-blue bar + live speed row, indistinguishable from a healthy
  // upload except for the tiny yellow banner. See regression test
  // "renders paused visuals ... telemetry is paused".
  const isPaused =
      upload.status === UploadClientStatus.PAUSED ||
      telemetry?.status === "paused";
  const isRehydrated = !!upload.rehydrated;
  // A rehydrated upload is "interrupted" (as opposed to a deliberate user
  // pause) when either: the persisted record was never marked paused, OR it
  // was marked paused by the auto-pause path fired from tus onError during
  // an unclean shutdown (page reload aborts every in-flight PATCH, which
  // classifyTusError treats as transient → pauseInFlight → IDB write with
  // pauseSource: "auto"). Without the second check the "Upload interrupted
  // by page reload" hint disappeared any time the browser reload race
  // happened to complete the IDB write before tear-down.
  const isInterruptedRecord =
      isRehydrated &&
      (!upload.pausedRecord?.paused || upload.pausedRecord?.pauseSource === "auto");
  const isFinalizing = telemetry?.status === "finalizing";
  // Three signals feed the queued visualisation (bug 2026-08-05 + reload
  // follow-up 2026-08-05b):
  //   1. `isQueuedInServiceQueue` — sourced from ChunkedUploadService's
  //      live queue snapshot via Redux. Source of truth once uploads have
  //      been submitted to the queue.
  //   2. `isActiveInServiceQueue` — same source; if we hold the slot we're
  //      NEVER queued (overrides everything below).
  //   3. `isPositionallyQueued` — after page reload nothing is in the
  //      service queue yet (module state was wiped, packages are just
  //      rehydrated as PAUSED). Yet the user's mental model is that any
  //      not-first paused package was queued and should still look queued.
  //      Derive: if there are 2+ in-progress uploads and this one isn't
  //      the oldest, treat it as queued until the service queue takes
  //      over on the first Resume click.
  //   4. Legacy `telemetry.status === "waiting"` for the tiny window
  //      between a new upload's click and the first queue-snapshot emit.
  const uploadQueueState = useUploadQueueState();
  const activeUploads = useActiveUploads();
  const isActiveInServiceQueue =
      uploadQueueState?.activeUploadId === upload.uploadId;
  const isQueuedInServiceQueue =
      uploadQueueState?.queuedUploadIds?.includes(upload.uploadId) || false;
  const oldestInProgressId = useMemo(() => {
    const inProgress = (activeUploads || []).filter(
      (u) =>
        u.status === UploadClientStatus.UPLOADING ||
        u.status === UploadClientStatus.PAUSED
    );
    if (inProgress.length < 2) return null;
    // Sort by createdAt so the queue order matches the same rule
    // autoResumeRehydratedWithHandles uses. Only meaningful when there
    // are 2+ candidates (single upload is never "queued behind" itself).
    return [...inProgress].sort(
      (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
    )[0]?.uploadId ?? null;
  }, [activeUploads]);
  const isPositionallyQueued =
      !isActiveInServiceQueue &&
      oldestInProgressId != null &&
      oldestInProgressId !== upload.uploadId;
  const isQueued =
      !isActiveInServiceQueue &&
      (isQueuedInServiceQueue || isPositionallyQueued);
  const isWaiting = telemetry?.status === "waiting" || isQueued;

  // Cross-tab ownership: when another tab is actively uploading this package, hide the
  // pause/resume/cancel actions in THIS tab — those should only be available where the
  // upload is actually running, to avoid races. SSE keeps the progress bar live here.
  const ownership = useUploadOwnership(upload.uploadId);
  const isOwnedByOtherTab = ownership === "other";
  // "unknown" = Web Locks supported but the first async poll hasn't landed yet. Hide
  // controls during this window too — otherwise a duplicated tab briefly shows Cancel
  // (which isn't compact-gated) between mount and the first poll. `canActOnUpload` is the
  // positive form: we know the lock is ours, up-for-grabs, or Web Locks aren't supported.
  const canActOnUpload =
      ownership === "me" || ownership === "none" || ownership === "unsupported";

  const showPauseControls = isChunkedUploaderActive() && !isFinalizing && canActOnUpload;

  // Orphan reconciliation: if the upload is marked UPLOADING in Redux but no tab holds
  // its Web Lock ("none"), the tab that owned it died mid-upload. Force PAUSED locally so
  // hasResumable evaluates true and the Resume button surfaces — otherwise a duplicated
  // tab would keep showing a Pause button that no-ops (its inFlight map is empty). Also
  // nudge ResumePreviousUploadBanner to re-run loadPending so this tab picks up the paused
  // record + FS handles from IndexedDB (our earlier boot rehydration may have run before
  // the sibling tab wrote the record). Without the rescan the Resume click has nothing to
  // hand to autoResumeRehydratedWithHandles and no session ever starts.
  //
  // Only fire on a real "other" → "none" transition. A bare "none" reading isn't enough:
  // ChunkedUploadService.resume awaits refreshTokenIfNeeded before it calls acquireUploadLock,
  // so between the resume click's status=UPLOADING dispatch and the actual lock acquire the
  // cached ownership is still "none" (from the pre-click poll). Firing here would immediately
  // flip status back to PAUSED and the freshly-started resume would look stuck as Resume.
  // Requiring prev==="other" limits reconciliation to the case where a sibling tab actually
  // died on us.
  //
  // Even with prev==="other" we can't reconcile eagerly: a reloaded original tab looks
  // identical to a closed one at the moment of lock release. Only after the original tab
  // remounts and calls claimUploadPresence does the query start returning "other" again.
  // Wait ~5s and re-query before pausing — that gives a reloading tab time to reclaim.
  const dispatch = useDispatch();
  const prevOwnershipRef = useRef(ownership);
  useEffect(() => {
    const prev = prevOwnershipRef.current;
    prevOwnershipRef.current = ownership;
    if (ownership !== "none" || prev !== "other") return;

    let cancelled = false;
    // Kept in sync with NONE_GRACE_MS in useUploadOwnership so the global Resume button and
    // the per-card reconciliation cross the takeover threshold together. If reconciliation
    // fired later, the header could briefly render a global Pause button while status was
    // still UPLOADING, then flip to Resume once we force PAUSED — confusing to the user.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const nowOwnership = await queryUploadOwnership(upload.uploadId);
      if (cancelled || nowOwnership !== "none") return; // sibling reclaimed presence
      if (upload.status === UploadClientStatus.UPLOADING) {
        dispatch(updateUploadingPackage({
          uploadId: upload.uploadId,
          status: UploadClientStatus.PAUSED,
          statusMessage: null,
        }));
      }
      if (!upload.rehydrated) {
        requestRehydration();
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ownership, upload.status, upload.rehydrated, upload.uploadId, dispatch]);

  const rehydratedFiles = upload.pausedRecord?.files || [];

  const sourceFileIds = useMemo(
      () => rehydratedFiles.map((f) => f.sourceFileId).filter(Boolean),
      [rehydratedFiles]
  );

  const hasPersistedHandles = hasAllFsHandles(upload.uploadId, sourceFileIds);

  // A rehydrated package whose files we can't reach from persisted FS Access handles. The
  // bytes are still held server-side (PAUSED is a resumable status for the whole 12h TTL), so
  // the only thing missing is the File objects — JS can't recreate those across a reload, and
  // only the user can hand them back via a picker gesture.
  //
  // Deliberately NOT gated on "is this Safari": handles are also absent on Chromium whenever
  // the source file was moved or renamed, the read permission lapsed, or the record came from
  // a drag-drop / pre-init path that never captured one. Browser-sniffing here would leave
  // exactly those Chromium users at the same dead end this control exists to remove — the
  // capability checks below are what decide HOW we open a picker, not WHETHER we offer one.
  const needsRepick = showPauseControls && isPaused && isRehydrated && !hasPersistedHandles;
  const canShowResume = compact && showPauseControls && isPaused && (!isRehydrated || hasPersistedHandles);

  const filesInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const submitPickedFiles = useCallback(
      (files, mode) => {
        const list = Array.from(files || []);

        if (list.length === 0) return;

        rehydratedResumeUpload({ uploadId: upload.uploadId, files: list, mode });
      },
      [rehydratedResumeUpload, upload.uploadId]
  );

  const handlePickFiles = useCallback(async () => {
    if (supportsFsHandles()) {
      try {
        const handles = await window.showOpenFilePicker({
          multiple: true,
          // Keep the "All files" toggle — the fingerprint check downstream is the real filter,
          // and an accept mismatch here would block the user from re-picking their own files.
          excludeAcceptAllOption: false,
        });
        const picked = await Promise.all(handles.map((h) => h.getFile()));
        submitPickedFiles(picked, "files");
      } catch (err) {
        // AbortError = the user dismissed the picker; leave the card paused and untouched.
        if (err && err.name !== "AbortError") {
          filesInputRef.current?.click();
        }
      }

      return;
    }

    filesInputRef.current?.click();
  }, [submitPickedFiles]);

  const handlePickFolder = useCallback(async () => {
    // showDirectoryPicker avoids Chrome's "Upload N files from this folder?" bulk-upload
    // confirmation that any <input webkitdirectory> triggers. Safari/Firefox don't have it,
    // so they fall through to the input, which is the only folder affordance available there.
    if (supportsDirectoryPicker()) {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: "read" });
        const picked = await collectFilesFromDirectory(dirHandle);
        submitPickedFiles(picked, "folder");
      } catch (err) {
        if (err && err.name !== "AbortError") {
          folderInputRef.current?.click();
        }
      }

      return;
    }

    folderInputRef.current?.click();
  }, [submitPickedFiles]);

  // Reset the input value after each pick so choosing the same file/folder twice in a row
  // still fires onChange (the browser suppresses it when the value is unchanged) — otherwise
  // a user correcting a wrong folder choice would find the second attempt silently dead.
  const handleInputChange = useCallback(
      (mode) => (event) => {
        const list = event?.target?.files;

        submitPickedFiles(list, mode);

        if (event?.target) event.target.value = "";
      },
      [submitPickedFiles]
  );

  const pausedExpiryText = useMemo(() => {
    if (!isPaused) return null;

    const pausedAt = upload.pausedRecord?.pausedAt;

    return formatPausedExpiry(pausedAt);
  }, [isPaused, upload.pausedRecord]);

  const pct = useMemo(() => {
    if (typeof upload.progress !== "number") return null;

    return Math.min(100, Math.max(0, upload.progress));
  }, [upload.progress]);

  const hasProgress = pct !== null;

  const statusMessage = getNetworkQualityMessage(networkQuality?.normalizedQuality);

  const statusSeverity = getNetworkQualitySeverity(
      networkQuality?.normalizedQuality
  );

  const isOffline =
      networkQuality?.normalizedQuality === NETWORK_QUALITY.OFFLINE;

  const baseTelemetryDisplay = getTelemetryDisplay(telemetry?.status);

  // Suppress the "interrupted by page reload" hint when another tab owns the upload — the
  // upload isn't actually interrupted here, it's still running in the source tab. The
  // "Uploading in another tab" notice below carries the right message for this state.
  const telemetryDisplay = isOwnedByOtherTab
      ? null
      : isInterruptedRecord
          ? { message: "Upload interrupted by page reload.", severity: "warning" }
          : baseTelemetryDisplay;

  const speedText = useMemo(() => {
    if (!telemetry) return null;

    const rawSpeed =
        typeof telemetry.rollingBps === "number"
            ? telemetry.rollingBps
            : typeof telemetry.averageBps === "number"
                ? telemetry.averageBps
                : null;

    if (rawSpeed === null) {
      return null;
    }

    return formatSpeed(rawSpeed);
  }, [telemetry]);

  // Slow-mode toggle: switch the uploaded-bytes display to KB when rollingBps drops below the
  // enter threshold, and only revert when it clearly recovers above the exit threshold. State
  // (not derived) so the two thresholds work as hysteresis; a plain derivation would flip the
  // unit every render as rolling speed jittered across a single boundary.
  const [showUploadedInKb, setShowUploadedInKb] = useState(false);
  useEffect(() => {
    const bps = typeof telemetry?.rollingBps === "number" ? telemetry.rollingBps : null;
    if (bps === null || bps <= 0) return;
    if (!showUploadedInKb && bps < KB_MODE_ENTER_BPS) {
      setShowUploadedInKb(true);
    } else if (showUploadedInKb && bps > KB_MODE_EXIT_BPS) {
      setShowUploadedInKb(false);
    }
  }, [telemetry?.rollingBps, showUploadedInKb]);

  // Bug 1 (2026-08): the aggregate bar would get stuck at the resume seed
  // value while per-file rows kept ticking. Root cause traced in the
  // observability timeline: uploadTelemetryService.recordProgress early-
  // returns when session status is PAUSED — and partial pause clicks left
  // telemetry paused while tus kept flushing. `computeEffectiveTelemetry`
  // reads per-file sum as a fallback so the main bar self-heals whenever
  // per-file has advanced past the telemetry snapshot. See
  // effectiveTelemetry.js for the rationale and unit tests.
  const effectiveTelemetry = useMemo(
      () => computeEffectiveTelemetry(telemetry, filesForSummary),
      [telemetry, filesForSummary]
  );

  const uploadedText = useMemo(
      () => formatUploadedInfo(effectiveTelemetry, { showUploadedInKb }),
      [effectiveTelemetry, showUploadedInKb]
  );

  return (
      <UploadProgressCardRow data-testid="upload-package-card-content" data-tour="upload-card">
        <span
            data-testid="upload-package-status"
            data-status={upload.status}
            data-telemetry-status={telemetry?.status || ""}
            style={{ display: "none" }}
        >
          {upload.status}
        </span>
        {/* Icon is now the primary pause/resume control (2026-08-04 UX pass).
            Uploading → pause icon-button, paused → play icon-button, queued/
            waiting → non-interactive hourglass. Direct manipulation removes
            the need to hunt for a separate Resume button in the actions row.
            Spectator mode, finalizing, and rehydrated-needing-repick keep a
            static status icon (no in-place action valid; the file-picker
            buttons in the actions row still handle that flow). */}
        <span data-tour="upload-state-control" style={{ display: "inline-flex", flexShrink: 0 }}>
        <UploadStateControl
            status={upload.status}
            isPaused={isPaused}
            isWaiting={isWaiting}
            isFinalizing={isFinalizing}
            canInteract={
              showPauseControls &&
              !isWaiting &&
              !isFinalizing &&
              !isOwnedByOtherTab &&
              !needsRepick
            }
            onPause={pauseAllUploads}
            onResume={resumeAllUploads}
        />
        </span>

        <UploadProgressInfo>
          <UploadProgressTitle>
            {upload.uploadMetadata.packageName}
            <TakenOverForContributorChip upload={upload} />
          </UploadProgressTitle>

          <LinearProgress
              key={hasProgress ? "det" : "ind"}
              variant={hasProgress ? "determinate" : "indeterminate"}
              value={hasProgress ? pct : 0}
              // "warning" (orange/yellow) matches the existing warning
              // severity used elsewhere for degraded / paused states. The
              // color change is the primary at-a-glance signal that the
              // upload is halted — the icon swap reinforces it (2026-08-04
              // UX pass). Reverts to the default primary colour on resume.
              color={isPaused ? "warning" : "primary"}
              sx={{
                mb: "8px",
                // Pin the track background to a translucent primary so the
                // unfilled portion of the bar looks identical whether the
                // upload is progressing or paused — only the filled bar
                // reflects the state change. Otherwise MUI re-tints the
                // track to warning too and the whole bar reads orange,
                // which the user found visually noisy.
                backgroundColor: (theme) =>
                  `${theme.palette.primary.main}44`,
              }}
          />

          {/* On the duplicated tab we hide the full stats row (time/MB/speed rely on
              this tab's local tus telemetry, which doesn't exist here). But `pct` comes
              from upload.progress, which IS mirrored via BroadcastChannel, so surface
              just the percentage on its own right-aligned row. */}
          {isOwnedByOtherTab && hasProgress && (
              <UploadProgressNumData style={{ justifyContent: "flex-end" }}>
                <span>{`${pct}%`}</span>
              </UploadProgressNumData>
          )}

          {/* Numeric MB/time/percent and speed stats come from this tab's local tus
              telemetry, which doesn't exist on a duplicated tab. Suppress them there so
              we don't show a misleading "0.00 MB of 594 MB" line — the mirrored progress
              bar above already carries the truthful state via BroadcastChannel. */}
          {!isOwnedByOtherTab && (
              <>
                <UploadProgressNumData>
                  <span>
                    {isPaused || isWaiting ? "" : formatRemainingTime(upload.remainingTime)}
                  </span>
                  <span>{uploadedText ?? "--"}</span>
                  <span data-testid="upload-progress-percent">{hasProgress ? `${pct}%` : ""}</span>
                </UploadProgressNumData>

                <Box
                    sx={{
                      my: 1.25,
                      height: "1px",
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                />

                {!isPaused && !isWaiting && !isFinalizing && (
                    <UploadProgressSecondaryInfo>
                      <UploadProgressSpeedDot />
                      <UploadProgressSpeedLabel>
                        Upload speed:
                      </UploadProgressSpeedLabel>
                      <UploadProgressSpeedValue>
                        {speedText ?? "--"}
                      </UploadProgressSpeedValue>
                    </UploadProgressSecondaryInfo>
                )}
              </>
          )}

          {!isPaused && isOffline && statusMessage && (
              <UploadProgressNetworkStatus severity={statusSeverity} role="status">
                {statusMessage}
              </UploadProgressNetworkStatus>
          )}

          {telemetryDisplay?.message && (
              <UploadProgressNetworkStatus
                  severity={telemetryDisplay.severity}
                  role="status"
              >
                {telemetryDisplay.message}
              </UploadProgressNetworkStatus>
          )}

          {/*
            Stall banner gate: uploadTelemetry now decays rollingBps to 0 when no bytes
            have arrived for the rolling window (see uploadTelemetry.js applyRollingSpeed),
            so a nonzero rollingBps here means bytes are genuinely still flowing. In that
            case suppress the alarming banner entirely — the auto-abort at 30s still runs
            silently if the watchdog decides to. This avoids the "shows Retry now while the
            speed indicator says 500 KB/s" false positive reported 2026-07-31.
          */}
          {stallStatus && !isPaused && !isOwnedByOtherTab && !(telemetry?.rollingBps > 0) && (
              <Box
                  data-testid="upload-stall-banner"
                  data-tour="upload-stall-banner"
                  data-stall-state={stallStatus}
              >
                {/* "Retry now" button removed 2026-08: it felt like a no-op on
                    the exact bad networks where users reached for it (tus HEAD
                    hangs on the same weak connection, so pressing it just
                    accelerates the same 30s watchdog auto-abort that runs
                    anyway). Now we tell the user recovery is automatic so
                    they don't feel abandoned staring at a stalled bar. */}
                <UploadProgressNetworkStatus severity="warning" role="status">
                  {stallStatus === "reconnecting"
                      ? "Reconnecting…"
                      : stallStatus === "stalled"
                          ? "Connection stalled — retrying automatically…"
                          : "Waiting for server response…"}
                </UploadProgressNetworkStatus>
              </Box>
          )}

          {upload.statusMessage && (
              <UploadProgressMessage>
                {upload.statusMessage}
              </UploadProgressMessage>
          )}

          {/* Per-file rows also read from local tus/IDB state and would render an empty
              or stale list on a duplicated tab. Hidden there — the mirrored package
              progress is the only truthful signal we have. */}
          {!isOwnedByOtherTab && (
              <Box
                  sx={{
                    mt: 1.25,
                    borderRadius: "10px",
                    overflow: "hidden",
                    background: "#2D3A4D",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
              >
                <Box
                    onClick={() => setIsFilesExpanded((v) => !v)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: "42px",
                      px: 1.9,
                      pr: 1.9,
                      py: 1.1,
                      cursor: "pointer",
                      userSelect: "none",
                      background: "#35445A",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        background: "#3B4C64",
                      },
                    }}
                    role="button"
                    aria-expanded={isFilesExpanded}
                >
                  <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 800,
                        letterSpacing: "0.045em",
                        textTransform: "uppercase",
                        color: "rgba(247,250,250,0.86)",
                      }}
                  >
                    Uploading files
                    {filesSummary
                        ? ` (${filesSummary.done}/${filesSummary.total})`
                        : ""}
                  </Typography>

                  <Box sx={{ flex: 1 }} />

                  <Box
                      component={ChevronIcon}
                      sx={{
                        width: 9,
                        height: 9,
                        mr: 0.4,
                        color: "rgba(247,250,250,0.7)",
                        transform: isFilesExpanded
                            ? "rotate(-180deg)"
                            : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                  />
                </Box>

                <Collapse in={isFilesExpanded} timeout={200} unmountOnExit>
                  <Box
                      sx={{
                        px: 1.25,
                        pb: 1,
                        pt: 0.75,
                        background: "#2D3A4D",
                        borderTop: "1px solid rgba(255,255,255,0.1)",
                      }}
                  >
                    <UploadFileRows
                      uploadId={upload.uploadId}
                      compact={compact}
                      showUploadedInKb={showUploadedInKb}
                    />
                  </Box>
                </Collapse>
              </Box>
          )}

          {isPaused && pausedExpiryText && (
              <UploadProgressMessage data-tour="upload-expires">
                {pausedExpiryText}
              </UploadProgressMessage>
          )}

          {isRehydrated && isPaused && canActOnUpload && (
              <UploadProgressMessage>
                {hasPersistedHandles
                    ? isInterruptedRecord
                        ? "Click Resume to continue from where the upload stopped — already uploaded chunks won't be re-sent."
                        : "Click Resume to continue from where you paused — already uploaded chunks won't be re-sent."
                    : "This upload was interrupted — select the same files again to continue. Already uploaded chunks won't be re-sent."}
              </UploadProgressMessage>
          )}

          {isOwnedByOtherTab && (
              <UploadProgressNetworkStatus
                  severity="warning"
                  role="status"
              >
                Uploading in another tab. Pause, resume, and cancel are only available
                in the tab where the upload was started.
              </UploadProgressNetworkStatus>
          )}

          <UploadProgressCardActions>
            {/* Pause / Resume footer buttons removed 2026-08-04: the state
                icon (top-left of the card) is now the primary pause/resume
                affordance. See UploadStateControl above. Only the file-
                picker + cancel actions remain in the footer. */}

            {needsRepick && (
                <>
                  <UploadProgressButton onClick={handlePickFolder} data-testid="upload-select-folder-button">
                    Select folder
                  </UploadProgressButton>

                  <UploadProgressSecondButton onClick={handlePickFiles} data-testid="upload-select-files-button">
                    Select files
                  </UploadProgressSecondButton>
                </>
            )}

            {canActOnUpload && (
                <UploadProgressSecondButton onClick={() => setIsConfirmCancel(true)} data-testid="upload-cancel-button" data-tour="upload-cancel">
                  Cancel
                </UploadProgressSecondButton>
            )}
          </UploadProgressCardActions>

          {needsRepick && (
              <>
                <input
                    type="file"
                    hidden
                    multiple
                    ref={filesInputRef}
                    onChange={handleInputChange("files")}
                />
                <input
                    type="file"
                    hidden
                    webkitdirectory=""
                    directory=""
                    ref={folderInputRef}
                    onChange={handleInputChange("folder")}
                />
              </>
          )}
        </UploadProgressInfo>

        <CancelUploadDialog
            open={isConfirmCancel}
            packageName={upload.uploadMetadata?.packageName}
            onClose={() => setIsConfirmCancel(false)}
            onConfirm={({ reason, notes }) => {
              setIsConfirmCancel(false);
              cancelUpload({ uploadId: upload.uploadId, reason, notes });
            }}
        />
      </UploadProgressCardRow>
  );
};

/**
 * Interactive state icon for the upload card (2026-08-04 UX pass).
 *
 * Replaces the previous purely-decorative `UploadingIcon` with a clickable
 * control that matches the current upload state:
 *
 *   uploading  → PauseCircleFilledIcon (click to pause)
 *   paused     → PlayCircleFilledIcon  (click to resume)
 *   waiting    → HourglassTopIcon      (non-interactive; queued behind another upload)
 *   finalizing → UploadingIcon         (non-interactive; server assembling)
 *   spectator  → PauseCircleFilledIcon or UploadingIcon (non-interactive)
 *
 * usePendingClick provides the guaranteed-visible spinner (min 500ms) so a
 * click on very poor networks always feels acknowledged, matching the top-
 * bar Pause All / Resume All buttons.
 */

// Attribution chip surfaced next to the package name when the current
// session is managing an upload it doesn't own — admin-takeover case
// (2026-08-06). `contributorId` is populated by the fetchActiveSessions
// hydrate path from the backend row's original contributor; if it differs
// from the signed-in user's uid, we render "for {name}" so the admin sees
// clearly whose upload they're driving. Silent no-op otherwise.
const TakenOverForContributorChip = ({ upload }) => {
  const { currentUser } = useAuthState();
  const currentUid = currentUser?.uid || null;
  const contributorId = upload?.contributorId || null;
  const contributorName = upload?.contributorName || contributorId;
  if (!contributorId || !currentUid) return null;
  if (contributorId === currentUid) return null;
  return (
    <Tooltip
      arrow
      title={`This upload was started by ${contributorName || contributorId}. Your admin session took over control; the row is still attributed to them.`}
    >
      <Chip
        label={`for ${contributorName || contributorId}`}
        size="small"
        variant="outlined"
        sx={{
          ml: 1,
          height: 20,
          fontSize: 11,
          borderColor: "rgba(247,250,250,0.32)",
          color: "rgba(247,250,250,0.85)",
          verticalAlign: "middle",
        }}
      />
    </Tooltip>
  );
};

export const UploadStateControl = ({
  status,
  isPaused,
  isWaiting,
  isFinalizing,
  canInteract,
  onPause,
  onResume,
}) => {
  const { pending: isResuming, trigger: triggerResume } = usePendingClick(
    () => onResume(),
    { minVisibleMs: 800, settled: !isPaused }
  );
  const { pending: isPausing, trigger: triggerPause } = usePendingClick(
    () => onPause(),
    { minVisibleMs: 800, settled: isPaused }
  );

  if (isResuming || isPausing) {
    return (
      <UploadProgressIconWrapper
          as="span"
          status={status}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress
            size={40}
            thickness={4}
            color="inherit"
            data-testid="upload-state-control-pending"
        />
      </UploadProgressIconWrapper>
    );
  }

  if (isWaiting) {
    return (
      <Tooltip title="Waiting for the previous upload to finish" arrow>
        <UploadProgressIconWrapper as={HourglassTopIcon} status={status} />
      </Tooltip>
    );
  }

  if (isFinalizing) {
    return <UploadProgressIconWrapper as={UploadingIcon} status={status} />;
  }

  if (!canInteract) {
    // Spectator mode / rehydrated-needing-repick / uploader kill switched
    // to legacy — non-interactive but still state-appropriate icon.
    return (
      <UploadProgressIconWrapper
          as={isPaused ? PauseCircleFilledIcon : UploadingIcon}
          status={status}
      />
    );
  }

  if (isPaused) {
    return (
      <Tooltip title="Resume upload" arrow>
        <IconButton
            onClick={triggerResume}
            size="small"
            aria-label="Resume upload"
            data-testid="upload-state-resume-button"
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              padding: 0,
              color: (theme) => theme.palette.warning.main,
              "&:hover": {
                backgroundColor: (theme) =>
                  `${theme.palette.warning.main}22`,
              },
            }}
        >
          <PlayCircleFilledIcon sx={{ fontSize: 52 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Pause upload" arrow>
      <IconButton
          onClick={triggerPause}
          size="small"
          aria-label="Pause upload"
          data-testid="upload-state-pause-button"
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            padding: 0,
            color: (theme) => theme.palette.primary.main,
            "&:hover": {
              backgroundColor: (theme) =>
                `${theme.palette.primary.main}22`,
            },
          }}
      >
        <PauseCircleFilledIcon sx={{ fontSize: 52 }} />
      </IconButton>
    </Tooltip>
  );
};